import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Activity, Pause, Play, RefreshCw, Search, Download, Filter, ArrowDownWideNarrow,
  Loader2, Wifi, WifiOff, AlertOctagon, Trash2, ChevronRight, ExternalLink, Clock,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useLiveAuditFeed } from "@/hooks/useLiveAuditFeed";
import {
  applyFilters, categoryOptions, severityChip, severityDot, outcomeText,
  formatDateTime, formatTime, eventsToCsv, downloadBlob,
} from "@/components/liveAudit/audit-utils";
import { StatusDot, statusLabel } from "@/components/liveAudit/LiveOpsConsole";
import {
  VLKPageHeader, VLKCard, VLKKPICard, VLKEmptyState, VLKButton,
} from "@/components/vlk";

const CAPACITY = 800;

/**
 * Dedicated Live Audit workspace — expanded operator view for investigators,
 * compliance officers, and system administrators. Same data plane as the
 * dockable Live Ops Console; wider surface for filtering, inspection, and
 * evidence-grade export.
 */
export default function LiveAuditPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const focusLedgerId = searchParams.get("ledger_id") || "";

  const feed = useLiveAuditFeed({ capacity: CAPACITY });
  const [autoScroll, setAutoScroll] = useState(true);
  const [filters, setFilters] = useState({
    search: focusLedgerId, severity: "", category: "", actor: "", institution: "",
  });
  const [selected, setSelected] = useState(null);
  const listRef = useRef(null);

  const filtered = useMemo(() => applyFilters(feed.events, filters), [feed.events, filters]);

  useEffect(() => {
    if (autoScroll && listRef.current && !feed.paused) {
      listRef.current.scrollTop = 0;
    }
  }, [filtered.length, autoScroll, feed.paused]);

  useEffect(() => {
    if (focusLedgerId) {
      const match = feed.events.find((e) => e.ledger_id === focusLedgerId);
      if (match) setSelected(match);
    }
  }, [focusLedgerId, feed.events]);

  const doExport = useCallback(() => {
    downloadBlob(`veralok_live_${Date.now()}.csv`, "text/csv", eventsToCsv(filtered));
  }, [filtered]);

  const doExportJson = useCallback(() => {
    downloadBlob(`veralok_live_${Date.now()}.json`, "application/json", JSON.stringify(filtered, null, 2));
  }, [filtered]);

  const stats = useMemo(() => {
    const s = { total: filtered.length, high: 0, critical: 0, failed: 0 };
    for (const e of filtered) {
      if (e.risk_level === "high") s.high += 1;
      if (e.risk_level === "critical") s.critical += 1;
      if (e.outcome && e.outcome !== "SUCCESS") s.failed += 1;
    }
    return s;
  }, [filtered]);

  return (
    <div data-testid="live-audit-page" className="pb-8">
      <VLKPageHeader
        title="Live Audit Feed"
        description="Real-time operational awareness across every VeraLok write action. Hydrated + streamed via the platform WebSocket broker."
        actions={
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-sm border border-border bg-card">
              <StatusDot status={feed.status} />
              <span className="text-[10px] uppercase tracking-widest font-mono text-muted-foreground">
                {statusLabel(feed.status)}
              </span>
              {feed.reconnectAttempts > 0 && feed.status === "reconnecting" && (
                <span className="text-[10px] font-mono text-warning">retry #{feed.reconnectAttempts}</span>
              )}
            </div>
            <VLKButton
              variant={feed.paused ? "outline" : "secondary"}
              size="md"
              data-testid={feed.paused ? "live-audit-resume" : "live-audit-pause"}
              onClick={feed.paused ? feed.resume : feed.pause}
            >
              {feed.paused ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
              {feed.paused ? "Resume" : "Pause"}
            </VLKButton>
            <VLKButton variant="outline" size="md" data-testid="live-audit-refresh" onClick={feed.refresh}>
              <RefreshCw className="h-4 w-4" /> Refresh
            </VLKButton>
            <VLKButton variant="outline" size="md" data-testid="live-audit-export-csv" onClick={doExport}>
              <Download className="h-4 w-4" /> CSV
            </VLKButton>
            <VLKButton variant="outline" size="md" data-testid="live-audit-export-json" onClick={doExportJson}>
              <Download className="h-4 w-4" /> JSON
            </VLKButton>
          </div>
        }
      />

      {feed.status === "forbidden" ? (
        <VLKEmptyState
          icon={AlertOctagon}
          title="Access restricted"
          description="Your role does not include audit:read. Contact a system administrator to request access."
        />
      ) : (
        <>
          {/* KPI strip */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
            <VLKKPICard testId="live-audit-kpi-total" label="Events in view" value={stats.total} icon={Activity} />
            <VLKKPICard testId="live-audit-kpi-high" label="High severity" value={stats.high} tone="warning" icon={AlertOctagon} />
            <VLKKPICard testId="live-audit-kpi-critical" label="Critical" value={stats.critical} tone="danger" icon={AlertOctagon} />
            <VLKKPICard testId="live-audit-kpi-failed" label="Failed outcomes" value={stats.failed} tone="danger" icon={WifiOff} />
          </div>

          {feed.paused && feed.bufferedDuringPause > 0 && (
            <div data-testid="live-audit-buffered-banner" className="mb-4 px-4 py-2 border border-warning/30 bg-warning/5 text-warning rounded-sm text-xs flex items-center gap-2">
              <Pause className="h-3.5 w-3.5" />
              Feed paused. {feed.bufferedDuringPause} event{feed.bufferedDuringPause === 1 ? "" : "s"} accumulated since pause. Resume to continue.
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_360px] gap-4">
            {/* Feed column */}
            <VLKCard className="flex flex-col min-h-[560px]">
              {/* Filter bar */}
              <div className="px-4 py-3 border-b border-border flex flex-wrap items-center gap-2">
                <div className="relative flex-1 min-w-[220px]">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                  <input
                    data-testid="live-audit-search"
                    value={filters.search}
                    onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
                    placeholder="Search action, actor, target, correlation…"
                    className="w-full h-9 pl-7 pr-2 rounded-sm bg-background border border-border text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  />
                </div>
                <select
                  data-testid="live-audit-severity"
                  value={filters.severity}
                  onChange={(e) => setFilters((f) => ({ ...f, severity: e.target.value }))}
                  className="h-9 rounded-sm border border-border bg-background text-sm px-2"
                >
                  <option value="">Severity</option>
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="critical">Critical</option>
                </select>
                <select
                  data-testid="live-audit-category"
                  value={filters.category}
                  onChange={(e) => setFilters((f) => ({ ...f, category: e.target.value }))}
                  className="h-9 rounded-sm border border-border bg-background text-sm px-2"
                >
                  <option value="">Category</option>
                  {categoryOptions().map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <input
                  data-testid="live-audit-actor"
                  value={filters.actor}
                  onChange={(e) => setFilters((f) => ({ ...f, actor: e.target.value }))}
                  placeholder="Actor email…"
                  className="h-9 rounded-sm border border-border bg-background text-sm px-2 w-[160px]"
                />
                <input
                  data-testid="live-audit-institution"
                  value={filters.institution}
                  onChange={(e) => setFilters((f) => ({ ...f, institution: e.target.value }))}
                  placeholder="Institution…"
                  className="h-9 rounded-sm border border-border bg-background text-sm px-2 w-[140px]"
                />
                <button
                  data-testid="live-audit-autoscroll"
                  onClick={() => setAutoScroll((v) => !v)}
                  className={cn(
                    "inline-flex items-center gap-1.5 h-9 px-3 rounded-sm border text-xs uppercase tracking-wider",
                    autoScroll ? "border-foreground text-foreground" : "border-border text-muted-foreground hover:bg-secondary"
                  )}
                  title="Auto-scroll"
                >
                  <ArrowDownWideNarrow className="h-3.5 w-3.5" />
                  Auto
                </button>
                <button
                  data-testid="live-audit-clear"
                  onClick={feed.clear}
                  className="inline-flex items-center gap-1.5 h-9 px-3 rounded-sm border border-border hover:bg-secondary text-xs uppercase tracking-wider text-muted-foreground"
                >
                  <Trash2 className="h-3.5 w-3.5" /> Clear
                </button>
                <div className="ml-auto text-[10px] font-mono text-muted-foreground tabular">
                  {filtered.length}/{feed.events.length}
                </div>
              </div>

              {/* Table */}
              <div ref={listRef} className="flex-1 overflow-auto max-h-[calc(100vh-380px)]">
                {feed.events.length === 0 && feed.status === "connecting" ? (
                  <div className="p-10 flex flex-col items-center gap-2 text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span className="text-sm">Hydrating from ledger…</span>
                  </div>
                ) : filtered.length === 0 ? (
                  <div className="p-10 text-center text-sm text-muted-foreground">No events match filters.</div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-muted/40 border-b border-border sticky top-0">
                      <tr>
                        {["Time","Sev","Category","Action","Actor","Role","Target","Outcome",""].map((h) => (
                          <th key={h} className="px-3 py-2 text-left text-[10px] uppercase tracking-wider text-muted-foreground font-medium">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((e) => (
                        <tr
                          key={e.ledger_id}
                          data-testid={`live-audit-row-${e.ledger_id}`}
                          onClick={() => setSelected(e)}
                          className={cn(
                            "border-b border-border last:border-0 hover:bg-secondary/50 transition-colors font-mono text-xs cursor-pointer",
                            selected?.ledger_id === e.ledger_id && "bg-secondary/70"
                          )}
                        >
                          <td className="px-3 py-2 whitespace-nowrap tabular text-muted-foreground">{formatTime(e.timestamp)}</td>
                          <td className="px-3 py-2">{severityChip(e.risk_level)}</td>
                          <td className="px-3 py-2 uppercase text-[10px] tracking-widest text-muted-foreground">{e.category}</td>
                          <td className="px-3 py-2 font-medium normal-case">{e.action}</td>
                          <td className="px-3 py-2 text-muted-foreground truncate max-w-[180px]">{e.actor_email || "system"}</td>
                          <td className="px-3 py-2 text-muted-foreground uppercase text-[10px] tracking-widest">{e.role || "—"}</td>
                          <td className="px-3 py-2 text-muted-foreground truncate max-w-[160px]">{e.target_id || "—"}</td>
                          <td className="px-3 py-2">{outcomeText(e.outcome)}</td>
                          <td className="px-3 py-2 text-muted-foreground"><ChevronRight className="h-3.5 w-3.5" /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              <footer className="border-t border-border px-4 py-2 flex items-center justify-between text-[10px] uppercase tracking-widest text-muted-foreground font-mono shrink-0">
                <span>Capacity {feed.events.length}/{CAPACITY}</span>
                <span>{feed.status === "live" ? "Streaming live" : statusLabel(feed.status)}</span>
              </footer>
            </VLKCard>

            {/* Inspector */}
            <VLKCard className="p-0 min-h-[560px] flex flex-col">
              <div className="px-4 py-3 border-b border-border">
                <div className="font-display text-sm">Event inspector</div>
                <div className="text-[11px] text-muted-foreground mt-0.5">Click a row to inspect. Click-through opens the linked entity workspace.</div>
              </div>
              {selected ? (
                <div className="flex-1 overflow-auto p-4 space-y-3">
                  <Row label="Ledger ID">{selected.ledger_id}</Row>
                  <Row label="Timestamp"><span className="font-mono">{formatDateTime(selected.timestamp)}</span></Row>
                  <Row label="Severity">{severityChip(selected.risk_level)}</Row>
                  <Row label="Category">{selected.category}</Row>
                  <Row label="Action">{selected.action}</Row>
                  <Row label="Actor">{selected.actor_email || "—"}</Row>
                  <Row label="Role">{selected.role || "—"}</Row>
                  <Row label="Target">{selected.target_type} · {selected.target_id || "—"}</Row>
                  <Row label="Institution">{selected.institution_id || "—"}</Row>
                  <Row label="Case">{selected.case_id || "—"}</Row>
                  <Row label="Outcome">{outcomeText(selected.outcome)}</Row>
                  <Row label="Correlation">{selected.correlation_id || "—"}</Row>
                  <Row label="Integrity">{selected.integrity_hash || "—"}</Row>
                  <div className="pt-3 border-t border-border">
                    <VLKButton variant="outline" size="sm" onClick={() => navigateToTarget(navigate, selected)} data-testid="live-audit-open-target">
                      <ExternalLink className="h-3.5 w-3.5" />
                      Open in workspace
                    </VLKButton>
                  </div>
                </div>
              ) : (
                <div className="flex-1 flex items-center justify-center p-6 text-center text-muted-foreground text-sm">
                  Select an event to see full details.
                </div>
              )}
            </VLKCard>
          </div>
        </>
      )}
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="grid grid-cols-[100px_1fr] gap-3 items-start text-xs">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground pt-0.5">{label}</div>
      <div className="font-mono break-all">{children}</div>
    </div>
  );
}

function navigateToTarget(navigate, e) {
  if (!e) return;
  if (e.target_type === "IDENTITY" && e.target_id) navigate(`/identities/${e.target_id}`);
  else if (e.target_type === "RECOVERY_REQUEST" && e.target_id) navigate(`/recovery/${e.target_id}`);
  else if (e.target_type === "INSTITUTION_REQUEST" && e.target_id) navigate(`/institutions`);
  else if (e.target_type === "API_CREDENTIAL") navigate(`/developer`);
  else if (e.target_type === "WEBHOOK") navigate(`/developer`);
}
